/**
 * ArtifactPreviewCard — V3 Page 07
 *
 * Renders a **compact** ArtifactSpec preview inside a chat bubble, with a
 * "Pin to sidebar" CTA. The card is a draft view — the spec hasn't hit the
 * server yet; pinning is what persists both the artifact + the sidebar row
 * in one atomic transaction (see pin-artifact-store.js).
 *
 * Goals:
 *   • Preview ≤ 320px tall, scrollable if content exceeds (DoD #9
 *     "ArtifactPreviewCard compact layout doesn't break on large
 *     infographics").
 *   • Pin button is keyboard-reachable + aria-labeled (DoD #10 a11y).
 *   • 200ms spinner shows after click; switches to "Still working…" if
 *     the POST stalls >2s (page-07 spec §states.pinning).
 *   • Surfaces idempotent result text: "Already in your sidebar under <group>".
 *   • 429 rate-limit: toast + disabled state until window resets.
 *
 * Not in scope for Page 07:
 *   • Real tool-result data for the widgets (stub draft widgets render
 *     with em-dash placeholders until the full compose_artifact handler
 *     lands in a follow-up fork).
 *   • Full ArtifactRenderer responsiveness inside chat (compact inline
 *     shell intentionally simplifies layout to vstack).
 */

import { useCallback, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Pin, Check, AlertCircle, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'

import {
  useSidebarPages,
  type PinArtifactResult,
} from '@/contexts/PageRegistryContext'

export interface ArtifactPreviewCardProps {
  /** Draft spec emitted by the chat orchestrator (schema-valid ArtifactSpec). */
  spec: Record<string, any>
  /** LLM-suggested label — shown on the card + used as default pin label. */
  suggestedLabel: string
  /** LLM-suggested group (Comparisons / Canvases / ...). */
  suggestedGroup: string
  /** Correlation id for the chat turn — persisted on the artifact row. */
  sourceTurnId?: string | null
  /** Invoked after a successful pin so the chat can thank the user. */
  onPinned?: (result: PinArtifactResult) => void
}

type PinState =
  | { status: 'idle' }
  | { status: 'pinning' }
  | { status: 'pinned'; result: PinArtifactResult }
  | { status: 'already-pinned'; result: PinArtifactResult }
  | { status: 'rate-limited'; retryAfterSeconds: number }
  | { status: 'error'; message: string }

function LabeledRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
        {children}
      </span>
    </div>
  )
}

function WidgetSummary({ widgets }: { widgets: any[] }) {
  // Compact summary line per widget type — avoids dragging the full
  // ArtifactRenderer into a 300px chat bubble. Real data populates after
  // pin + open; here we just show structure.
  return (
    <ul className="mt-2 space-y-1 text-sm">
      {widgets.slice(0, 6).map((w, i) => (
        <li
          key={w.id || i}
          className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-1 last:border-0"
        >
          <span className="text-slate-700 dark:text-slate-200">
            {w.label || w.title || w.headline || w.type}
          </span>
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {w.type}
          </span>
        </li>
      ))}
      {widgets.length > 6 ? (
        <li className="text-xs text-slate-500 dark:text-slate-400 italic">
          + {widgets.length - 6} more widgets
        </li>
      ) : null}
    </ul>
  )
}

export function ArtifactPreviewCard({
  spec,
  suggestedLabel,
  suggestedGroup,
  sourceTurnId,
  onPinned,
}: ArtifactPreviewCardProps) {
  const { pinArtifact } = useSidebarPages()
  const [state, setState] = useState<PinState>({ status: 'idle' })

  const widgets = Array.isArray(spec?.widgets) ? spec.widgets : []
  const kind = typeof spec?.kind === 'string' ? spec.kind : 'artifact'
  const description = typeof spec?.description === 'string' ? spec.description : null

  const pinLabel = useMemo(() => {
    if (state.status === 'pinning') return 'Pinning…'
    if (state.status === 'pinned') return 'Pinned'
    if (state.status === 'already-pinned') return 'Already pinned'
    if (state.status === 'rate-limited') return 'Rate limited'
    return 'Pin to sidebar'
  }, [state.status])

  const handlePin = useCallback(async () => {
    if (state.status === 'pinning' || state.status === 'pinned') return
    setState({ status: 'pinning' })
    try {
      const result = await pinArtifact({
        spec,
        label: suggestedLabel,
        group: suggestedGroup,
        source_turn_id: sourceTurnId || undefined,
      })
      if (result.already_pinned) {
        setState({ status: 'already-pinned', result })
      } else {
        setState({ status: 'pinned', result })
      }
      onPinned?.(result)
    } catch (err) {
      const anyErr = err as Error & { retryAfterSeconds?: number }
      if (anyErr.message === 'rate_limit_exceeded') {
        setState({
          status: 'rate-limited',
          retryAfterSeconds: anyErr.retryAfterSeconds ?? 3600,
        })
      } else {
        setState({ status: 'error', message: anyErr.message || 'pin_failed' })
      }
    }
  }, [state.status, pinArtifact, spec, suggestedLabel, suggestedGroup, sourceTurnId, onPinned])

  const isPinning = state.status === 'pinning'
  const isTerminal = state.status === 'pinned' || state.status === 'already-pinned'

  return (
    <motion.div
      layout
      data-testid="artifact-preview-card"
      className={clsx(
        'mt-3 rounded-xl border bg-white/95 dark:bg-slate-800/95 overflow-hidden',
        'border-slate-200 dark:border-slate-600 shadow-outdoor-sm',
      )}
    >
      <div className="px-3 py-2 bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {kind} preview
        </span>
        <span className="text-xs text-slate-400 dark:text-slate-500">draft</span>
      </div>
      <div
        className="px-3 py-3 max-h-[280px] overflow-y-auto"
        data-testid="artifact-preview-body"
      >
        <LabeledRow label="Title">{suggestedLabel}</LabeledRow>
        <LabeledRow label="Group">{suggestedGroup}</LabeledRow>
        {description ? (
          <p className="mt-2 text-xs text-slate-600 dark:text-slate-300 italic">
            {description}
          </p>
        ) : null}
        {widgets.length > 0 ? <WidgetSummary widgets={widgets} /> : null}
      </div>
      <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2 bg-white dark:bg-slate-800">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {state.status === 'already-pinned' && state.result?.existing_group
            ? `Already in your sidebar under ${state.result.existing_group}`
            : state.status === 'pinned'
              ? 'Saved — check your sidebar'
              : state.status === 'rate-limited'
                ? `Try again in ~${Math.max(1, Math.round(state.retryAfterSeconds / 60))} min`
                : state.status === 'error'
                  ? 'Pin failed — retry?'
                  : 'Tap to save this to your sidebar.'}
        </div>
        <button
          type="button"
          onClick={handlePin}
          disabled={isPinning || state.status === 'pinned' || state.status === 'rate-limited'}
          aria-label={
            state.status === 'pinned' || state.status === 'already-pinned'
              ? `${suggestedLabel} — already pinned to sidebar`
              : `Pin ${suggestedLabel} to sidebar`
          }
          data-testid="pin-to-sidebar-button"
          className={clsx(
            'inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-carnival-500',
            'transition-colors',
            isTerminal
              ? 'bg-emerald-500 text-white hover:bg-emerald-500/90 disabled:opacity-80'
              : state.status === 'error'
                ? 'bg-red-500 text-white hover:bg-red-500/90'
                : state.status === 'rate-limited'
                  ? 'bg-slate-300 text-slate-600 cursor-not-allowed'
                  : 'bg-carnival-400 text-white hover:bg-carnival-500 disabled:opacity-60',
          )}
        >
          {isPinning ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
          ) : isTerminal ? (
            <Check className="w-3.5 h-3.5" aria-hidden />
          ) : state.status === 'error' ? (
            <AlertCircle className="w-3.5 h-3.5" aria-hidden />
          ) : (
            <Pin className="w-3.5 h-3.5" aria-hidden />
          )}
          <span>{pinLabel}</span>
        </button>
      </div>
    </motion.div>
  )
}
